import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Cookies from 'js-cookie';
import { FaRegFilePdf } from 'react-icons/fa';
import { IoMdCheckmarkCircle } from 'react-icons/io';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import logo from '../../../images/bluelogo.png';
import FinishCard from '../finishCard';
import { generatePDF } from '../pdfGenerator';
import NewAvaliacaoPDF from './newAvaliacaoPDF';
import NewNotificacaoPreenchido from '../notificacao/newNotificacaoPreenchido';
import NewClassificacaoPreenchido from '../classificacao/newClassificacaoPreenchido';
import NewInvestigacaoPreenchido from '../investigacao/newInvestigacaoPreenchido';
import {
	requestInvByProtocol,
	requestNewAvalCreation,
	requestAvalList,
	requestAtribuidos,
	requestEmailSend,
} from '../../../services/requests';
import { requestInputsChoices } from '../../../services/newNotesRequest';

const formSchema = z
	.object({
		acoes_realizadas: z.enum(['sim', 'nao'], {
			required_error: 'Selecione uma opção',
		}),
		padrao_trabalho: z.enum(['sim', 'nao'], {
			required_error: 'Selecione uma opção',
		}),
		observacoes: z.string().optional(),
		corrigir_classificacao: z.boolean().default(false),
		classificacao_final: z.string().optional(),
		sub_classificacao_final: z.string().optional(),
		tipo_incidente_final: z.string().optional(),
		tipo_incidente_final_outros: z.string().optional(),
	})
	.superRefine((data, ctx) => {
		if (!data.corrigir_classificacao) return;

		if (!data.classificacao_final) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['classificacao_final'],
				message: 'Selecione a nova classificação',
			});
		}

		if (
			data.classificacao_final === 'NEVER_EVENTS' &&
			!data.sub_classificacao_final
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['sub_classificacao_final'],
				message: 'Selecione o Never Event',
			});
		}

		if (!data.tipo_incidente_final) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['tipo_incidente_final'],
				message: 'Selecione o novo tipo de incidente',
			});
		}

		if (
			data.tipo_incidente_final === 'OUTROS' &&
			!data.tipo_incidente_final_outros?.trim()
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['tipo_incidente_final_outros'],
				message: 'Informe o outro tipo de incidente',
			});
		}
	});

const getInvestigatorEmails = (investigadores) => {
	const list = Array.isArray(investigadores)
		? investigadores
		: Array.isArray(investigadores?.investigadores)
			? investigadores.investigadores
			: Array.isArray(investigadores?.results)
				? investigadores.results
				: [];

	return Array.from(
		new Set(
			list
				.map((item) => item?.email || item?.usuario?.email || '')
				.filter(Boolean),
		),
	);
};

const avaliacaoListInFlight = new Map();
const inputsChoicesInFlight = new Map();
const investigacaoByProtocolInFlight = new Map();

const getRequestCacheKey = (...parts) =>
	parts.map((part) => String(part || '').trim()).join('|');

const requestAvalListOnce = (token) => {
	const key = getRequestCacheKey('avaliacoes', token);
	if (avaliacaoListInFlight.has(key)) {
		return avaliacaoListInFlight.get(key);
	}

	const requestPromise = requestAvalList(token).finally(() => {
		avaliacaoListInFlight.delete(key);
	});
	avaliacaoListInFlight.set(key, requestPromise);
	return requestPromise;
};

const requestInputsChoicesOnce = () => {
	const key = 'inputs-choices';
	if (inputsChoicesInFlight.has(key)) {
		return inputsChoicesInFlight.get(key);
	}

	const requestPromise = requestInputsChoices().finally(() => {
		inputsChoicesInFlight.delete(key);
	});
	inputsChoicesInFlight.set(key, requestPromise);
	return requestPromise;
};

const requestInvByProtocolOnce = (token, protocolo) => {
	const key = getRequestCacheKey('investigacao', token, protocolo);
	if (investigacaoByProtocolInFlight.has(key)) {
		return investigacaoByProtocolInFlight.get(key);
	}

	const requestPromise = requestInvByProtocol(token, protocolo).finally(() => {
		investigacaoByProtocolInFlight.delete(key);
	});
	investigacaoByProtocolInFlight.set(key, requestPromise);
	return requestPromise;
};

export default function NewAvaliacao({ notificacao, cards }) {
	const protocolo = notificacao?.protocolo;
	const [choices, setChoices] = useState(null);
	const [sending, setSending] = useState(false);
	const [returning, setReturning] = useState(false);
	const [submitError, setSubmitError] = useState('');
	const [enviado, setEnviado] = useState(false);
	const [retornado, setRetornado] = useState(false);
	const [submittedAvaliacao, setSubmittedAvaliacao] = useState(null);
	const [observacaoRetorno, setObservacaoRetorno] = useState('');
	const [observacaoRetornoErro, setObservacaoRetornoErro] = useState('');
	const [showReturnObs, setShowReturnObs] = useState(false);
	const [showReturnConfirm, setShowReturnConfirm] = useState(false);
	const [renderPdf, setRenderPdf] = useState(false);

	const avaliacaoRef = useRef(null);
	const pdfReadyRef = useRef(false);
	const loadedAvaliacaoKeyRef = useRef('');

	const waitForPdfReady = async () => {
		const startedAt = Date.now();
		while (!pdfReadyRef.current && Date.now() - startedAt < 15000) {
			await new Promise((resolve) => setTimeout(resolve, 100));
		}
	};

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		reset,
		getValues,
		formState: { errors, isValid },
	} = useForm({
		resolver: zodResolver(formSchema),
		mode: 'onChange',
		defaultValues: {
			acoes_realizadas: undefined,
			padrao_trabalho: undefined,
			observacoes: '',
			corrigir_classificacao: false,
			classificacao_final: '',
			sub_classificacao_final: '',
			tipo_incidente_final: '',
			tipo_incidente_final_outros: '',
		},
	});

	const corrigirClassificacao = watch('corrigir_classificacao');
	const classificacaoFinal = watch('classificacao_final');
	const tipoIncidenteFinal = watch('tipo_incidente_final');
	const userCookie = Cookies.get('user');
	const parsedUser = userCookie ? JSON.parse(userCookie) : null;
	const permissionMode = parsedUser?.permissao?.includes('Classificadores')
		? 'classificador'
		: parsedUser?.permissao?.includes('Investigadores')
			? 'investigador'
			: null;
	const currentMode =
		cards?.caso === 'NOTIFICADO'
			? 'classificador'
			: cards?.caso === 'CLASSIFICADO'
				? 'investigador'
				: permissionMode;
	const canEditAvaliacao = currentMode !== 'investigador';
	const canDownloadPdf = currentMode === 'investigador';

	useEffect(() => {
		const loadData = async () => {
			try {
				const userCookie = Cookies.get('user');
				if (!userCookie || !protocolo) return;

				const { token } = JSON.parse(userCookie);
				const loadKey = `${token}|${protocolo}`;
				if (loadedAvaliacaoKeyRef.current === loadKey) return;
				loadedAvaliacaoKeyRef.current = loadKey;

				const [avaliacoes, inputsChoices] = await Promise.all([
					requestAvalListOnce(token),
					requestInputsChoicesOnce(),
				]);

				setChoices(inputsChoices);

				if (!Array.isArray(avaliacoes)) return;
				const avaliacao = avaliacoes.find(
					(item) => item?.protocolo === protocolo,
				);
				if (!avaliacao) return;

				reset({
					acoes_realizadas:
						avaliacao.acoes_realizadas === true ? 'sim' : 'nao',
					padrao_trabalho:
						avaliacao.padrao_trabalho === true ? 'sim' : 'nao',
					observacoes: avaliacao.observacoes || '',
					corrigir_classificacao: Boolean(
						avaliacao.classificacao_final ||
						avaliacao.tipo_incidente_final ||
						avaliacao.tipo_incidente_final_outros,
					),
					classificacao_final: avaliacao.classificacao_final || '',
					sub_classificacao_final: (() => {
						const raw = avaliacao.sub_classificacao_final;
						if (!raw) return '';
						// Se já é PK numérico usa direto
						if (/^\d+$/.test(String(raw))) return String(raw);
						// Código string (ex: "NE_17") → busca o PK
						const entry = Object.entries(
							inputsChoices?.sub_classificacao || {},
						).find(([, item]) => item.codigo === raw);
						return entry ? entry[0] : '';
					})(),
					tipo_incidente_final: avaliacao.tipo_incidente_final || '',
					tipo_incidente_final_outros:
						avaliacao.tipo_incidente_final_outros || '',
				});
			} catch (error) {
				console.error('Erro ao carregar avaliação final:', error);
			}
		};

		loadData();
	}, [protocolo, reset]);

	useEffect(() => {
		if (classificacaoFinal !== 'NEVER_EVENTS') {
			setValue('sub_classificacao_final', '', { shouldValidate: true });
		}
		if (tipoIncidenteFinal !== 'OUTROS') {
			setValue('tipo_incidente_final_outros', '', {
				shouldValidate: true,
			});
		}
	}, [classificacaoFinal, tipoIncidenteFinal, setValue]);

	const classificacaoOptions = useMemo(
		() =>
			choices?.classificacao
				? Object.entries(choices.classificacao).map(
						([value, label]) => ({
							value,
							label,
						}),
					)
				: [],
		[choices],
	);

	const neverEventOptions = useMemo(
		() =>
			choices?.sub_classificacao
				? Object.entries(choices.sub_classificacao).map(
						([pk, item]) => ({ pk, ...item }),
					)
				: [],
		[choices],
	);

	const tipoIncidenteOptions = useMemo(
		() =>
			choices?.tipo_incidente
				? Object.entries(choices.tipo_incidente).map(
						([value, label]) => ({
							value,
							label,
						}),
					)
				: [],
		[choices],
	);

	const handleDownloadPdf = async () => {
		pdfReadyRef.current = false;
		setRenderPdf(false);
		await new Promise((resolve) => requestAnimationFrame(resolve));
		setRenderPdf(true);
		await new Promise((resolve) => requestAnimationFrame(resolve));
		await waitForPdfReady();
		await new Promise((resolve) => requestAnimationFrame(resolve));
		await generatePDF([avaliacaoRef], `${protocolo || 'avaliacao'}.pdf`);
	};

	const getInvestigacaoId = async (token) => {
		const investigacoes = await requestInvByProtocolOnce(
			token,
			protocolo,
		);
		if (!Array.isArray(investigacoes) || investigacoes.length === 0) {
			throw new Error('Investigação não encontrada para este protocolo.');
		}
		return investigacoes[0]?.id;
	};

	const handleReturnToInvestigation = async () => {
		if (!canEditAvaliacao) return;

		const obs = observacaoRetorno.trim();
		if (!obs) {
			setObservacaoRetornoErro(
				'Informe a observação para retornar à investigação.',
			);
			return;
		}

		setObservacaoRetornoErro('');
		setSubmitError('');
		setReturning(true);

		try {
			const userCookie = Cookies.get('user');
			if (!userCookie) {
				setSubmitError(
					'Usuário não autenticado. Faça login novamente.',
				);
				return;
			}
			const { token } = JSON.parse(userCookie);
			const investigacaoId = await getInvestigacaoId(token);
			const result = await requestNewAvalCreation(token, {
				investigacao: investigacaoId,
				decisao: 'REJEITADA',
				acoes_realizadas: false,
				pontos_trabalho: false,
				padrao_trabalho: false,
				observacoes: getValues('observacoes') || obs,
				classificacao_adequada: true,
				tipo_incidente_adequado: true,
				motivo_rejeicao: obs,
			});
			if (result?.error) {
				setSubmitError(
					'Não foi possível retornar a avaliação. Tente novamente.',
				);
				return;
			}
			try {
				const investigadores = await requestAtribuidos(
					token,
					notificacao.protocolo,
				);
				const destinatarios = getInvestigatorEmails(investigadores);

				if (destinatarios.length > 0) {
					await requestEmailSend(token, {
						template_id: 4,
						destinatarios,
						variaveis: {
							unidade: notificacao?.unidade_nome
								? notificacao.unidade_nome
								: 'unidade nao informada',
							protocolo: notificacao.protocolo,
							justificativa: obs,
						},
					});
				}
			} catch (emailError) {
				console.error(
					'Erro ao enviar e-mail de retorno da avaliacao:',
					emailError,
				);
			}

			setRetornado(true);
		} catch (error) {
			console.error(error);
			setSubmitError(
				error.message ||
					'Não foi possível retornar a avaliação. Tente novamente mais tarde.',
			);
		} finally {
			setReturning(false);
		}
	};

	const onSubmit = async (data) => {
		if (!canEditAvaliacao) {
			return;
		}

		setSubmitError('');
		setSending(true);

		try {
			const userCookie = Cookies.get('user');
			if (!userCookie) {
				setSubmitError(
					'Usuário não autenticado. Faça login novamente.',
				);
				return;
			}

			const { token } = JSON.parse(userCookie);
			const investigacaoId = await getInvestigacaoId(token);
			const payload = {
				investigacao: investigacaoId,
				decisao: 'ACEITA',
				acoes_realizadas: data.acoes_realizadas === 'sim',
				pontos_trabalho: data.padrao_trabalho === 'sim',
				padrao_trabalho: data.padrao_trabalho === 'sim',
				observacoes: data.observacoes || '',
				classificacao_adequada: !data.corrigir_classificacao,
				tipo_incidente_adequado: !data.corrigir_classificacao,
				classificacao_final: data.corrigir_classificacao
					? data.classificacao_final
					: '',
				sub_classificacao_final:
					data.sub_classificacao_final.replace(/^NE_/, '') || '',
				tipo_incidente_final: data.corrigir_classificacao
					? data.tipo_incidente_final
					: '',
				tipo_incidente_final_outros:
					data.corrigir_classificacao &&
					data.tipo_incidente_final === 'OUTROS'
						? data.tipo_incidente_final_outros || ''
						: '',
			};

			const result = await requestNewAvalCreation(token, payload);
			if (result?.error) {
				setSubmitError(
					'Não foi possível enviar a avaliação final. Tente novamente.',
				);
				return;
			}

			setSubmittedAvaliacao(data);
			setEnviado(true);
		} catch (error) {
			console.error(error);
			setSubmitError(
				'Não foi possível enviar a avaliação final. Tente novamente mais tarde.',
			);
		} finally {
			setSending(false);
		}
	};

	if (enviado) {
		return (
			<FinishCard
				stage='avaliacaoFinal'
				requestResult={{
					notificacao,
					avaliacao: submittedAvaliacao,
				}}
			/>
		);
	}

	if (retornado) {
		return (
			<div className='flex justify-center items-start w-[80%] mx-auto h-screen'>
				<div className='flex flex-col bg-[#F5F5F5] mt-[100px] items-center rounded-md max-w-[800px] w-full gap-5 flex-grow shadow-lg'>
					<div className='bg-[#004A80] rounded-t-md w-full h-[20px]' />
					<IoMdCheckmarkCircle className='text-[#00C0F3] size-[50px] sm:size-[100px]' />
					<p className='text-3xl font-semibold text-center text-[#004A80]'>
						Avaliação Retornada para Investigação
					</p>
					<p className='text-lg font-medium w-[80%] text-center text-[#8b8b8b]'>
						A observação foi enviada com sucesso e o caso voltou
						para a etapa de investigação.
					</p>
					<a
						href='/notes-avaliation'
						className='text-[#00C0F3] font-medium hover:underline mb-8 mt-2'>
						voltar à lista de avaliações
					</a>
				</div>
			</div>
		);
	}

	return (
		<div className='w-[95%] max-w-[1136px] font-inter mx-auto mt-[80px] flex flex-col gap-10'>
			<div className='bg-[#F0F4F8] flex rounded-sm w-full content-center min-h-[142px] p-[32px] gap-[40px] justify-between lg:flex-row flex-col'>
				<div className='content-center items-center mx-auto'>
					<img
						className='w-[200px]  sm:w-[300px] h-auto'
						src={logo}
						alt='logo'
					/>
				</div>
				<p className='text-[28px] sm:text-[32px] font-bold text-[#13335A] content-center max-w-[714px] text-center'>
					NOTIFICAÇÃO DE INCIDENTES E NÃO CONFORMIDADES
				</p>
			</div>

			<NewNotificacaoPreenchido notificacao={notificacao} />
			<NewClassificacaoPreenchido notificacao={notificacao} />
			<NewInvestigacaoPreenchido notificacao={notificacao} />

			{canDownloadPdf && (
				<div className='w-full flex justify-start'>
					<button
						type='button'
						className='enabled:bg-[#E3E3E3] max-h-[40px] text-[#a3a3a3] enabled:text-[#757575] flex bg-[#d3d3d3] py-2 px-2 rounded-md enabled:hover:bg-[#757575] enabled:hover:text-[#f0f0f0] transition-all duration-200 focus:outline-none font-semibold mt-5'
						onClick={handleDownloadPdf}>
						{
							<>
								<FaRegFilePdf className='w-[40px] h-auto text-[10px]' />{' '}
								PDF
							</>
						}
					</button>
				</div>
			)}

			{submitError && (
				<div className='mb-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700'>
					{submitError}
				</div>
			)}

			{renderPdf && (
				<div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
					<div ref={avaliacaoRef}>
						<NewAvaliacaoPDF
							notificacao={notificacao}
							hideAvaliacao={canDownloadPdf}
							onReady={() => {
								pdfReadyRef.current = true;
							}}
						/>
					</div>
				</div>
			)}

			{canEditAvaliacao && (
				<form
					onSubmit={handleSubmit(onSubmit)}
					className='flex flex-col gap-6'>
					<section className='border border-[#E3E3E3] rounded-md'>
						<div className='text-lg font-semibold border-b rounded-t-lg text-center bg-[#F4F4F4] text-[#004A80] p-1 border-[#E3E3E3]'>
							<h2>AVALIAÇÃO FINAL</h2>
						</div>
						<div className='p-3 flex flex-col gap-4'>
							<div className='flex  gap-2'>
								<p className='text-[15px] text-[#004A80] font-medium'>
									As ações realizadas foram suficientes para
									bloquear a reincidência do incidente?
								</p>
								<div className='flex gap-4 text-[#757575]'>
									<label className='flex items-center gap-2'>
										<input
											type='radio'
											value='sim'
											{...register('acoes_realizadas')}
										/>
										Sim
									</label>
									<label className='flex items-center gap-2'>
										<input
											type='radio'
											value='nao'
											{...register('acoes_realizadas')}
										/>
										Não
									</label>
								</div>
								{errors.acoes_realizadas && (
									<p className='text-red-500 text-xs'>
										{errors.acoes_realizadas.message}
									</p>
								)}
							</div>

							<div className='flex gap-2'>
								<p className='text-[15px] text-[#004A80] font-medium'>
									Foi criado ou alterado algum padrão de
									trabalho para evitar a reincidência do
									incidente?
								</p>
								<div className='flex gap-4 text-[#757575]'>
									<label className='flex items-center gap-2'>
										<input
											type='radio'
											value='sim'
											{...register('padrao_trabalho')}
										/>
										Sim
									</label>
									<label className='flex items-center gap-2'>
										<input
											type='radio'
											value='nao'
											{...register('padrao_trabalho')}
										/>
										Não
									</label>
								</div>
								{errors.padrao_trabalho && (
									<p className='text-red-500 text-xs'>
										{errors.padrao_trabalho.message}
									</p>
								)}
							</div>

							<div className='flex flex-col gap-2'>
								<textarea
									{...register('observacoes')}
									className='w-full min-h-[140px] border border-[#E3E3E3] rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#004A80]'
									placeholder='Observações'
								/>
							</div>
						</div>
					</section>

					{/* Bloco sem título: pergunta sobre reclassificação */}
					<section className='border border-[#E3E3E3] rounded-md bg-white'>
						<div className='p-4 flex flex-wrap items-center gap-4'>
							<p className='text-[15px] text-[#004A80] font-medium'>
								Corrigir tipo ou a classificação do incidente?
							</p>
							<div className='flex gap-5 text-[#757575]'>
								<label className='flex items-center gap-2 cursor-pointer'>
									<input
										type='radio'
										checked={corrigirClassificacao === true}
										onChange={() =>
											setValue(
												'corrigir_classificacao',
												true,
												{ shouldValidate: true },
											)
										}
									/>
									Sim
								</label>
								<label className='flex items-center gap-2 cursor-pointer'>
									<input
										type='radio'
										checked={
											corrigirClassificacao === false
										}
										onChange={() =>
											setValue(
												'corrigir_classificacao',
												false,
												{ shouldValidate: true },
											)
										}
									/>
									Não
								</label>
							</div>
						</div>
					</section>

					{/* Bloco NOVA CLASSIFICAÇÃO */}
					{corrigirClassificacao && (
						<>
							<section className='border border-[#E3E3E3] rounded-md'>
								<div className='text-lg font-semibold border-b rounded-t-lg text-center bg-[#F4F4F4] text-[#004A80] p-1 border-[#E3E3E3]'>
									<h2>NOVA CLASSIFICAÇÃO</h2>
								</div>
								<div className='p-3 flex flex-col gap-4'>
									<div className='flex flex-col gap-2'>
										<p className='text-[15px] text-[#004A80] font-medium'>
											Classificação
										</p>
										<select
											{...register('classificacao_final')}
											className='border rounded-md border-[#E3E3E3] py-2 px-2 h-[40px] w-full'>
											<option value=''>
												Selecione a classificação
											</option>

											{classificacaoOptions.map(
												(item) =>
													item.value !==
														'OUTRA_NATUREZA' && (
														<option
															key={item.value}
															value={item.value}
															title={item.label}>
															{item.label.length >
															100
																? item.label.slice(
																		0,
																		130,
																	) + '...'
																: item.label}
														</option>
													),
											)}
										</select>
										{errors.classificacao_final && (
											<p className='text-red-500 text-xs'>
												{
													errors.classificacao_final
														.message
												}
											</p>
										)}
									</div>

									<div className='flex flex-col gap-2'>
										<p className='text-[15px] text-[#004A80] font-medium'>
											Never Event
										</p>
										<select
											{...register(
												'sub_classificacao_final',
											)}
											disabled={
												classificacaoFinal !==
												'NEVER_EVENTS'
											}
											className='border rounded-md border-[#E3E3E3] py-2 px-2 h-[40px] w-full disabled:bg-[#F9F9F9] disabled:text-[#AAAAAA]'>
											<option value=''>
												Selecione o Never Event
											</option>
											{neverEventOptions.map((item) => (
												<option
													key={item.pk}
													value={item.pk}>
													{item.nome}
												</option>
											))}
										</select>
										{errors.sub_classificacao_final && (
											<p className='text-red-500 text-xs'>
												{
													errors
														.sub_classificacao_final
														.message
												}
											</p>
										)}
									</div>
								</div>
							</section>

							{/* Bloco NOVO TIPO DE INCIDENTE */}
							<section className='border border-[#E3E3E3] rounded-md'>
								<div className='text-lg font-semibold border-b rounded-t-lg text-center bg-[#F4F4F4] text-[#004A80] p-1 border-[#E3E3E3]'>
									<h2>NOVO TIPO DE INCIDENTE</h2>
								</div>
								<div className='p-3 flex flex-col gap-4'>
									<div className='flex flex-col gap-2'>
										<p className='text-[15px] text-[#004A80] font-medium'>
											Incidente
										</p>
										<select
											{...register(
												'tipo_incidente_final',
											)}
											className='border rounded-md border-[#E3E3E3] py-2 px-2 h-[40px] w-full'>
											<option value=''>
												Selecione o Incidente
											</option>
											{tipoIncidenteOptions.map(
												(item) => (
													<option
														key={item.value}
														value={item.value}>
														{item.label}
													</option>
												),
											)}
										</select>
										{errors.tipo_incidente_final && (
											<p className='text-red-500 text-xs'>
												{
													errors.tipo_incidente_final
														.message
												}
											</p>
										)}
									</div>

									{tipoIncidenteFinal === 'OUTROS' && (
										<div className='flex flex-col gap-2'>
											<p className='text-[15px] text-[#004A80] font-medium'>
												Outro tipo de incidente
											</p>
											<input
												{...register(
													'tipo_incidente_final_outros',
												)}
												className='border rounded-md border-[#E3E3E3] py-2 px-2 h-[40px] w-full'
												placeholder='Informe o tipo de incidente'
											/>
											{errors.tipo_incidente_final_outros && (
												<p className='text-red-500 text-xs'>
													{
														errors
															.tipo_incidente_final_outros
															.message
													}
												</p>
											)}
										</div>
									)}
								</div>
							</section>
						</>
					)}

					{/* Rodapé: retornar (esq) + concluir (dir) */}
					<div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
						{/* Retornar para investigação */}
						<div className='flex flex-col gap-2 sm:w-[260px]'>
							<button
								type='button'
								disabled={returning || sending}
								onClick={() => {
									if (!showReturnObs) {
										// Mostrar textarea
										setShowReturnObs(true);
										setObservacaoRetornoErro('');
									} else if (!observacaoRetorno.trim()) {
										// Textarea vazia: esconder novamente
										setShowReturnObs(false);
										setObservacaoRetornoErro('');
									} else {
										// Textarea preenchida: mostrar popup de confirmação
										setShowReturnConfirm(true);
									}
								}}
								className={`w-full rounded px-4 py-2 text-sm font-medium transition border ${
									returning || sending
										? 'cursor-not-allowed border-[#D9D9D9] text-[#BBBBBB]'
										: showReturnObs &&
											  observacaoRetorno.trim()
											? 'bg-[#004A80] border-[#004A80] text-[white] hover:bg-[#043d66]'
											: 'border-[#C0C0C0] text-[#757575] hover:border-[#cccccc] hover:text-[white] hover:bg-[#cccccc]'
								}`}>
								{returning
									? 'Retornando...'
									: 'Retornar para investigação'}
							</button>
							{showReturnObs && (
								<>
									<div className='border border-[#E3E3E3] rounded-md p-2 flex flex-col gap-1'>
										<p className='text-xs text-[#757575]'>
											Observação:
										</p>
										<textarea
											value={observacaoRetorno}
											onChange={(e) => {
												setObservacaoRetorno(
													e.target.value,
												);
												if (e.target.value.trim())
													setObservacaoRetornoErro(
														'',
													);
											}}
											className='w-full min-h-[90px] resize-none text-sm focus:outline-none text-gray-500'
											placeholder='Insira sua observação aqui'
										/>
									</div>
									{observacaoRetornoErro && (
										<p className='text-red-500 text-xs'>
											{observacaoRetornoErro}
										</p>
									)}
								</>
							)}
						</div>

						{/* Concluir */}
						<button
							type='submit'
							disabled={!isValid || sending || returning}
							className={`sm:w-[260px] w-full rounded px-4 py-2 text-sm font-medium transition ${
								!isValid || sending || returning
									? 'text-gray-300 border cursor-not-allowed'
									: 'bg-[#004A80] text-white hover:bg-[#193b53]'
							}`}>
							{sending ? 'Enviando...' : 'Concluir Notificação'}
						</button>
					</div>
				</form>
			)}

			{/* Popup de confirmação — Retornar para investigação */}
			{showReturnConfirm && (
				<div className='fixed inset-0 flex items-center justify-center bg-black/40 z-50'>
					<div className='relative z-10 font-inter bg-white rounded-md border border-[#E3E3E3] p-6 w-[90%] max-w-[450px] flex flex-col items-center gap-5'>
						<HiOutlineExclamationCircle className='text-[#B74644] w-[80px] h-auto' />
						<p className='text-[#B74644] font-semibold text-[22px]'>
							ATENÇÃO
						</p>
						<p className='text-[#757575] text-[16px] text-center'>
							Tem certeza que deseja retornar esta notificação
							para investigação?
						</p>
						<div className='flex gap-6 justify-center w-full mt-2'>
							<button
								type='button'
								onClick={() => setShowReturnConfirm(false)}
								className='text-[#757575] border border-[#e9e9e9] rounded min-w-[160px] font-inter px-4 py-2 hover:bg-[#d4d4d4] hover:text-white transition-all duration-300'>
								Cancelar
							</button>
							<button
								type='button'
								disabled={returning}
								onClick={() => {
									setShowReturnConfirm(false);
									handleReturnToInvestigation();
								}}
								className={`border rounded min-w-[160px] font-inter px-4 py-2 flex items-center justify-center gap-2 transition-all duration-300 ${
									returning
										? 'opacity-70 cursor-wait bg-[#004A80] border-[#004A80] text-white'
										: 'border-[#e9e9e9] text-[#757575] hover:bg-[#004A80] hover:border-[#004A80] hover:text-white'
								}`}>
								{returning ? (
									<>
										<span className='inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin' />
										<span>Retornando...</span>
									</>
								) : (
									<span>Confirmar</span>
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
